import {
  IRPMDevice,
  RPM_DEVICE_STATUS,
  RPM_DEVICE_TYPE,
} from "@home-health-notify/domain";
import React, { useContext, useState } from "react";
import { Box, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveDeviceDialog from "./RemoveDeviceDialog";
import VitalReadings from "./VitalReadings";
import { RightDrawerContext } from "../../../context/RightDrawerContext";
import {
  DeviceContainer,
  DeviceName,
  DeviceWrapper,
  SeeReadingsMessage,
} from "./Vitals.style";

type RpmDeviceTypeDescriptions = {
  [key in RPM_DEVICE_TYPE]: string;
};

export const rpmDeviceTypeDescriptions: RpmDeviceTypeDescriptions = {
  [RPM_DEVICE_TYPE.BLOOD_PRESSURE_DEVICE]: "Blood Pressure Monitor",
  [RPM_DEVICE_TYPE.WEIGHING_SCALE_DEVICE]: "Weighing Scale",
  [RPM_DEVICE_TYPE.PULSE_OXIMETER_DEVICE]: "Pulse Oximeter",
  [RPM_DEVICE_TYPE.THERMOMETER_DEVICE]: "Thermometer",
  [RPM_DEVICE_TYPE.NO_DEVICE_FOUND]: "No Device Found",
};

const AssignedDeviceItem = ({
  rpmDevice,
  canRemoveDevice = false,
  onRemoveDevice,
}: {
  rpmDevice: IRPMDevice;
  canRemoveDevice?: boolean;
  onRemoveDevice: (deviceId: string) => void;
}) => {
  const { setContent } = useContext(RightDrawerContext);

  const [isRemoveDeviceDialogOpen, setIsRemoveDeviceDialogOpen] =
    useState<boolean>(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);

  const openRemoveDeviceDialog = (e: React.MouseEvent, deviceId: string) => {
    e.stopPropagation();
    setSelectedDeviceId(deviceId);
    setIsRemoveDeviceDialogOpen(true);
  };

  const closeRemoveDeviceDialog = () => {
    setIsRemoveDeviceDialogOpen(false);
  };

  const onClickViewVital = () => {
    setContent(
      <VitalReadings
        deviceName={rpmDevice.rpmDeviceType}
        deviceId={rpmDevice.rpmDeviceId}
      />,
    );
  };

  const onRemoveDeviceClick = () => {
    if (selectedDeviceId) {
      onRemoveDevice(selectedDeviceId);
      setSelectedDeviceId(null);
    }
  };

  const deviceId = rpmDevice.rpmDeviceId;
  const multipleDeviceIds = deviceId?.split(",");

  return (
    <Box>
      {!multipleDeviceIds && (
        <DeviceWrapper onClick={onClickViewVital} data-testid={"vitals-device"}>
          <DeviceName>
            {rpmDeviceTypeDescriptions[rpmDevice.rpmDeviceType]} {deviceId}
          </DeviceName>
          <SeeReadingsMessage>
            Tap here to see readings available
          </SeeReadingsMessage>
        </DeviceWrapper>
      )}
      {deviceId && multipleDeviceIds && multipleDeviceIds?.length > 0 && (
        <>
          {multipleDeviceIds.map(deviceId => (
            <Box key={deviceId}>
              <DeviceContainer
                onClick={onClickViewVital}
                data-testid={"vitals-device"}
              >
                <DeviceWrapper>
                  <DeviceName>
                    {`${rpmDeviceTypeDescriptions[rpmDevice.rpmDeviceType]} (${deviceId})`}
                  </DeviceName>
                  <SeeReadingsMessage>
                    Tap here to see readings available
                  </SeeReadingsMessage>
                </DeviceWrapper>
                {canRemoveDevice &&
                  rpmDevice.status === RPM_DEVICE_STATUS.ACTIVE && (
                    <IconButton
                      onClick={e => openRemoveDeviceDialog(e, deviceId)}
                    >
                      <DeleteIcon color="error" />
                    </IconButton>
                  )}
              </DeviceContainer>
            </Box>
          ))}
        </>
      )}
      <RemoveDeviceDialog
        isOpen={isRemoveDeviceDialogOpen}
        onClose={closeRemoveDeviceDialog}
        onRemoveDevice={onRemoveDeviceClick}
      />
    </Box>
  );
};

export default AssignedDeviceItem;
